// ---------------------------------------------------------------------------
// Smart document import: students' resumes and personal statements are PDFs
// and DOCX files, so we parse those locally (pdfjs / mammoth) in addition to
// plain text. Parsing libraries are loaded lazily so they don't weigh down
// first paint.
// ---------------------------------------------------------------------------

import { readFileAsText } from './download';

async function readPdf(file: File): Promise<string> {
  const pdfjs = await import('pdfjs-dist');
  const workerUrl = (await import('pdfjs-dist/build/pdf.worker.min.mjs?url')).default;
  pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

  const doc = await pdfjs.getDocument({ data: await file.arrayBuffer() }).promise;
  const pages: string[] = [];
  for (let i = 1; i <= doc.numPages; i++) {
    const page = await doc.getPage(i);
    const content = await page.getTextContent();
    pages.push(
      content.items
        .map((item) => ('str' in item ? item.str : ''))
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim(),
    );
  }
  await doc.destroy();
  return pages.join('\n\n').trim();
}

async function readDocx(file: File): Promise<string> {
  const mammoth = (await import('mammoth/mammoth.browser')).default;
  const result = await mammoth.extractRawText({ arrayBuffer: await file.arrayBuffer() });
  return result.value.trim();
}

/** Extract text from .pdf, .docx, or plain-text files. */
export async function readDocumentText(file: File): Promise<string> {
  const name = file.name.toLowerCase();
  if (name.endsWith('.pdf')) return readPdf(file);
  if (name.endsWith('.docx')) return readDocx(file);
  return readFileAsText(file);
}

/** The accept attribute matching readDocumentText's capabilities. */
export const DOCUMENT_ACCEPT = '.txt,.md,.markdown,.pdf,.docx,text/plain,application/pdf';
